import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, ExternalLink, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { usePortal } from "@/context/PortalContext";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);
const INPUT = "bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-emerald-500";

export default function ContractorInvoices() {
  const { clients, selectedClientId } = usePortal();
  const client = clients.find((c) => c.id === selectedClientId);
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});

  const load = useCallback(() => { if (selectedClientId) api.get(`/contractor-invoices?client_id=${selectedClientId}`).then((r) => { setRows(r.data); setDraft(Object.fromEntries(r.data.map((x) => [x.id, { status: x.status || "unpaid", payment_date: x.payment_date || "", payment_channel: x.payment_channel || "" }]))); }).catch(() => {}); else setRows([]); }, [selectedClientId]);
  useEffect(() => { load(); }, [load]);

  const setField = (id, k, v) => setDraft((d) => ({ ...d, [id]: { ...d[id], [k]: v } }));
  const save = async (id) => {
    try {
      await api.patch(`/contractor-invoices/${id}`, draft[id]);
      toast.success("Invoice updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
  };
  const viewPdf = async (id) => {
    try {
      const { data } = await api.get(`/contractor-invoices/${id}/pdf`);
      if (!data.pdf) return toast.error("Invoice not available");
      const blob = await (await fetch(data.pdf)).blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Could not open invoice"); }
  };

  return (
    <div className="max-w-6xl" data-testid="contractor-invoices-page">
      <h1 className="text-3xl font-semibold tracking-tight font-display text-slate-900 flex items-center gap-2">
        <FileText className="h-7 w-7 text-emerald-600" /> Contractor Invoices
      </h1>
      <p className="mt-2 text-sm text-slate-500">Invoices raised by legal representatives for the selected client.</p>

      {!selectedClientId ? (
        <div className="mt-8 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-5 text-sm" data-testid="contractor-admin-select-client-message">
          Please select a client from the <strong>Viewing client</strong> dropdown at the top-right first.
        </div>
      ) : (
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl shadow-sm overflow-x-auto">
          {rows.length === 0 ? (
            <p className="text-sm text-slate-400 p-6" data-testid="contractor-invoices-empty">No contractor invoices for {client?.name || "this client"} yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="p-3 font-medium">Raised by</th>
                  <th className="p-3 font-medium">Property</th>
                  <th className="p-3 font-medium text-right">Total</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium">Payment date</th>
                  <th className="p-3 font-medium">Payment channel</th>
                  <th className="p-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid="contractor-invoice-row" className="border-b border-slate-50 last:border-0">
                    <td className="p-3 text-slate-700">{r.legal_rep_name}</td>
                    <td className="p-3 text-slate-700 max-w-[180px] truncate">{r.property_name}</td>
                    <td className="p-3 text-right font-mono text-slate-800">{inr(r.total_inr)}</td>
                    <td className="p-3">
                      <select value={draft[r.id]?.status || "unpaid"} onChange={(e) => setField(r.id, "status", e.target.value)} data-testid={`contractor-status-${r.id}`} className={INPUT}>
                        <option value="unpaid">Unpaid</option>
                        <option value="paid">Paid</option>
                      </select>
                    </td>
                    <td className="p-3">
                      <input type="date" value={draft[r.id]?.payment_date || ""} onChange={(e) => setField(r.id, "payment_date", e.target.value)} data-testid={`contractor-paydate-${r.id}`} className={INPUT} />
                    </td>
                    <td className="p-3">
                      <input value={draft[r.id]?.payment_channel || ""} onChange={(e) => setField(r.id, "payment_channel", e.target.value)} placeholder="e.g. Bank transfer" data-testid={`contractor-channel-${r.id}`} className={`${INPUT} w-36`} />
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => save(r.id)} data-testid={`contractor-save-${r.id}`} className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg px-3 py-1.5"><Save className="h-3.5 w-3.5" /> Save</button>
                        <button onClick={() => viewPdf(r.id)} data-testid={`contractor-viewpdf-${r.id}`} className="text-slate-400 hover:text-emerald-700" title="View PDF"><ExternalLink className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
